// libs
import React, { memo, useEffect, useState } from 'react';
import isEmpty from 'lodash/isEmpty';

// components
import Remove from 'shared/layouts/Wishlist/Mobile/components/Remove';
import ActionContainer from 'shared/layouts/Wishlist/components/ActionContainer';
import Placeholder from 'shared/layouts/Wishlist/Mobile/components/Products/Placeholder';
import SizeSelector from 'shared/layouts/Wishlist/components/SizeSelector';
import PriceReveal from 'shared/components/Product/components/PriceReveal';
import PromotionText from 'shared/components/PromotionText';
import PriceDrop from 'shared/layouts/Wishlist/components/PriceDrop';

// constants
import { CLASSNAMES } from 'shared/layouts/Listing/constants';
import { PRODUCT_INTERACTION } from 'shared/helpers/analytics/constants';
import { API_ERROR_MESSAGE } from 'shared/constants/messages';

//styles
import { Container, Product, PromotionTextDesktop } from './styles';

//hooks
import { useRemoteConfig } from 'shared/store/remoteConfigs/hooks';
import { useWishlistContext } from 'shared/layouts/Wishlist/API/context';
import useFetchWishlistProducts from 'shared/layouts/Wishlist/hooks/useFetchWishlistProducts';
import { TOASTER_TYPES, useToastContext } from 'shared/components/Toast';

//helpers
import {
  sizeSelectorBottomSheetClose,
  sizeSelectorBottomSheetOpen,
} from 'shared/layouts/Wishlist/analytics';
import { getDeviceType } from 'shared/helpers/app';
import NFA from 'shared/helpers/analytics/NFAnalytics';
import { getPromotionTextConfig } from 'shared/layouts/Wishlist/Mobile/components/Products/helpers';

// defs
interface IWishlistProduct {
  keyParam: number;
  item: Listing.IProduct;
  productWidth: number;
  openInTab: boolean;
}

function WishlistProduct({ keyParam, item, productWidth, openInTab }: IWishlistProduct) {
  const { showPrice, showActionButton } = useWishlistContext();
  const isMobile = getDeviceType() === 'MSITE';

  const {
    mobile = false,
    desktop = false,
    salePromotionConfig = {},
  } = useRemoteConfig('priceReveal');

  const isPriceDropNudgeEnabled = useRemoteConfig('priceDropNudgeEnabled');

  const { showToast } = useToastContext();

  const isPriceRevealEnabled = isMobile ? mobile : desktop;

  const [showSizeSelector, setShowSizeSelector] = useState(false);
  const [selectedSize, setSelectedSize] = useState<ProductDetail.ISizeOption>();
  const { isFetching, product, fetchProduct } = useFetchWishlistProducts(item.productId, false);
  const showPriceReveal =
    !item.isOutOfStock && isPriceRevealEnabled && !isEmpty(item?.priceRevealDTO);
  const showPriceDrop =
    isPriceDropNudgeEnabled &&
    isMobile &&
    !item.isOutOfStock &&
    item.priceDropFlag &&
    !showPriceReveal;

  const { showPromotion, promotionTextConfig } = getPromotionTextConfig({
    showPriceReveal,
    isPriceRevealEnabled,
    salePromotionConfig,
    isMobile,
    isOutOfStock: item?.isOutOfStock,
  });

  useEffect(() => {
    if (showSizeSelector) {
      sizeSelectorBottomSheetOpen();
    }
  }, [showSizeSelector]);

  const hideSheet = () => {
    //to close bottom sheet
    setShowSizeSelector(false);

    //to sent analytics of sheet
    sizeSelectorBottomSheetClose(showPriceReveal);
  };

  const addToBagHandler = () => {
    fetchProduct()
      .then(() => {
        setShowSizeSelector(true);
        NFA.updateDataLayer({ productType: item?.type });
      })
      .catch((e) => {
        showToast({ type: TOASTER_TYPES.ERROR, message: e?.message || API_ERROR_MESSAGE });
      });
  };

  const onProductInteraction = () => {
    NFA.updateDatalayerBeforeNext({ productInteractedType: PRODUCT_INTERACTION.WISHLIST_PRODUCT });
  };

  const wishlistChildren = showActionButton ? (
    <ActionContainer
      addToBagClickHandler={addToBagHandler}
      isOutOfStock={item.isOutOfStock}
      actionUrl={item.actionUrl}
      openInTab={openInTab}
      productId={item.productId}
      setShowSizeSelector={setShowSizeSelector}
      isLoading={isFetching}
      isPriceRevealProduct={isPriceRevealEnabled ? showPriceReveal : undefined}
      isSalePriceLive={salePromotionConfig?.mobile && showPromotion}
      isPriceDropped={item.priceDropFlag}
      priceDropValue={item.priceDropDifference}
    />
  ) : null;

  return (
    <Container maxWidth={productWidth}>
      <Product
        fallback={<Placeholder />}
        key={keyParam}
        item={item}
        priceReveal={
          showPriceReveal ? <PriceReveal priceRevealData={item?.priceRevealDTO} /> : null
        }
        width={productWidth}
        className={CLASSNAMES.WISHLIST}
        openInTab={openInTab}
        imageChildren={<Remove item={item} />}
        showPrice={showPrice}
        onInteraction={onProductInteraction}
        promotionText={
          showPromotion ? (
            isMobile ? (
              <PromotionText promotionTextConfig={promotionTextConfig} />
            ) : (
              <PromotionTextDesktop promotionTextConfig={promotionTextConfig} />
            )
          ) : null
        }
      />
      {showPriceDrop && <PriceDrop price={item.priceDropDifference || 0} />}
      {wishlistChildren}
      {showSizeSelector && (
        <SizeSelector
          selectedSize={selectedSize}
          setSelectedSize={setSelectedSize}
          product={product}
          onHide={hideSheet}
        />
      )}
    </Container>
  );
}

export default memo(WishlistProduct);
